
/**
 * Metric events for tracking user actions and system performance
 */
export enum MetricEvent {
    // Transaction Metrics
    TRANSACTION_CREATED = 'transaction.created',
    TRANSACTION_DELETED = 'transaction.deleted',
    TRANSACTION_EDITED = 'transaction.edited',

    // Budget Metrics
    BUDGET_CREATED = 'budget.created',
    BUDGET_UPDATED = 'budget.updated',
    BUDGET_EXCEEDED = 'budget.exceeded',
    CATEGORY_CREATED = 'category.created',

    // Feature Engagement
    DASHBOARD_VIEWED = 'dashboard.viewed',
    REPORT_GENERATED = 'report.generated',
    STATEMENT_UPLOADED = 'statement.uploaded',
    STATEMENT_PARSING_SUCCESS = 'statement.parsing.success',
    STATEMENT_PARSING_FAILED = 'statement.parsing.failed',
    AI_INSIGHT_GENERATED = 'ai_insight.generated',
    COACHING_VIEWED = 'coaching.viewed',
    GOAL_CREATED = 'goal.created',
    GOAL_ACHIEVED = 'goal.achieved',

    // Performance
    API_RESPONSE_TIME = 'api.response_time',
    PAGE_LOAD_TIME = 'page.load_time',
    EXPORT_SUCCESS = 'export.success',
    EXPORT_FAILED = 'export.failed',

    // User Behavior
    CURRENCY_CONVERSION_USED = 'currency_conversion.used',
    SEARCH_PERFORMED = 'search.performed',
    FILTER_APPLIED = 'filter.applied',

    // Subscription (if applicable)
    SUBSCRIPTION_UPGRADED = 'subscription.upgraded',
    SUBSCRIPTION_DOWNGRADED = 'subscription.downgraded',
    SUBSCRIPTION_CANCELLED = 'subscription.cancelled',
    TRIAL_STARTED = 'trial.started',
    TRIAL_CONVERTED = 'trial.converted',
}

/**
 * Service for tracking application metrics with Sentry
 */
class MetricsService {
    /**
     * Track a counter event (increments by 1)
     * @param event - The metric event to track
     * @param tags - Optional key-value pairs for categorization
     */
    trackEvent(event: MetricEvent, tags?: Record<string, string>): void {
        try {
            // @ts-ignore - Sentry metrics types are conflicting
            const metrics = (Sentry as any).metrics;
            if (metrics && metrics.increment) {
                metrics.increment(event, 1, { tags });
            }
        } catch (error) {
            console.warn('Failed to track metric event:', error);
        }
    }

    /**
     * Track a timing/duration metric
     * @param event - The metric event to track
     * @param duration - Duration in milliseconds
     * @param tags - Optional key-value pairs for categorization
     */
    trackTiming(event: MetricEvent, duration: number, tags?: Record<string, string>): void {
        try {
            // @ts-ignore - Sentry metrics types are conflicting
            const metrics = (Sentry as any).metrics;
            if (metrics && metrics.distribution) {
                metrics.distribution(event, duration, {
                    unit: 'millisecond',
                    tags
                });
            }
        } catch (error) {
            console.warn('Failed to track timing metric:', error);
        }
    }

    /**
     * Track a gauge metric (current snapshot value)
     * @param metric - The metric name
     * @param value - Current value
     * @param tags - Optional key-value pairs for categorization
     */
    trackGauge(metric: string, value: number, tags?: Record<string, string>): void {
        try {
            // @ts-ignore - Sentry metrics types are conflicting
            const metrics = (Sentry as any).metrics;
            if (metrics && metrics.gauge) {
                metrics.gauge(metric, value, { tags });
            }
        } catch (error) {
            console.warn('Failed to track gauge metric:', error);
        }
    }

    /**
     * Track financial health metrics
     */
    trackFinancialHealth(score: number, grade: string): void {
        this.trackGauge('financial_health.score', score, { grade });
    }

    /**
     * Track budget adherence rate
     */
    trackBudgetAdherence(rate: number): void {
        this.trackGauge('budget_adherence.rate', rate);
    }

    /**
     * Track active budget count
     */
    trackActiveBudgets(count: number): void {
        this.trackGauge('active_budgets.count', count);
    }

    /**
     * Track savings rate
     */
    trackSavingsRate(rate: number): void {
        this.trackGauge('savings_rate', rate);
    }
}

// Export singleton instance
export const metricsService = new MetricsService();
